import React, {useEffect, useState, useCallback} from 'react';
import {useParams, Link} from 'react-router-dom';
import axios from 'axios';
import {DragDropContext, Droppable, Draggable} from '@hello-pangea/dnd';
import AdminMenu from '../AdminMenu';
import AdminHeader from '../AdminHeader';
import {ADMIN_API_BASE} from "../../../utils/apiConfig";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faChevronLeft, faClose,
    faEllipsis, faFilter, faLanguage,
    faPencil,
    faPlus,
    faReorder,
    faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import ButtonLink from "../../../components/elements/ButtonLink";
import Button from "../../../components/elements/Button";
import InputField from "../../../components/elements/InputField";
import Modal from "../../../components/modal/Modal";
import SectionCard from "../../../components/admin/SectionCard";
import Toast from "../../../components/layout/Toast";
import SectionEditForm from "../../../components/admin/SectionEditForm";
import {TEXT} from "../../../utils/constants";
import useCourseSections from "../../../hooks/useCourseSections";

const rtlProps = {dir: 'rtl', lang: 'fa'};

export default function CourseSections() {
    const {courseId} = useParams();
    const {
        sections,
        setSections,
        loading,
        fetchSections,
        deleteSection
    } = useCourseSections(courseId);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [currentSection, setCurrentSection] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [toast, setToast] = useState(null);

    const getCsrfToken = async () => {
        const res = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            credentials: 'include'
        });
        const data = await res.json();
        return data.csrfToken;
    };

    const showToast = (message, type = 'success') => {
        setToast({message, type});
        setTimeout(() => setToast(null), 3500);
    };

    const openEdit = (section) => {
        setCurrentSection(section);
        setOpenEditModal(true);
    };

    const openDelete = (section) => {
        setCurrentSection(section);
        setOpenDeleteModal(true);
    };

    // API helpers
    const handleCreateSection = async (data) => {
        try {
            const token = await getCsrfToken();
            const res = await axios.post(
                `${ADMIN_API_BASE}/course/${courseId}/section/`,
                {
                    id: data.id,
                    is_active: data.is_active
                },
                {
                    headers: {'csrf-token': token},
                    withCredentials: true
                }
            );

            console.log("CREATE SECTION RESPONSE", res.data);

            return res.data;
        } catch (err) {
            console.error("CREATE SECTION ERROR", err);

            throw err;
        }
    };


    const handleUpdateSection = async (sectionId, sectionPayload) => {
        const token = await getCsrfToken();
        const res = await axios.put(
            `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/`,
            sectionPayload,
            {headers: {'csrf-token': token}, withCredentials: true}
        );
        return res.data.section;
    };

    const createContentsForSection = async (sectionId, contents) => {
        if (!contents || contents.length === 0) return [];
        const token = await getCsrfToken();
        const created = [];

        for (const c of contents) {
            const payload = {
                id: String(c.id || '').trim(),
                type: Number(c.type),
                // level optional – backend can assign next free level safely
            };


            console.log(payload);
            const res = await axios.post(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/`,
                payload,
                {headers: {'csrf-token': token}, withCredentials: true}
            );
            created.push(res.data.content);
        }

        return created;
    };

    const deleteContentsForSection = async (sectionId, ids) => {
        for (const id of ids) {
            await axios.delete(
                `${ADMIN_API_BASE}/course/${courseId}/content/${id}`
            );
        }
    };

    const handleDeleteSection = async (sectionId) => {
        try {
            await axios.delete(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/`
            );

            await fetchSections();

            setOpenDeleteModal(false);

            setCurrentSection(null);

            showToast('سرفصل حذف شد');
        } catch (err) {
            console.error(err);

            showToast('خطا در حذف', 'error');
        }
    };

    // Drag & Drop
    const onDragEnd = async (result) => {
        const {source, destination, type} = result;
        if (!destination) return;

        // SECTION REORDER
        if (type === "SECTION") {
            const newOrder = Array.from(sections);
            const [moved] = newOrder.splice(source.index, 1);
            newOrder.splice(destination.index, 0, moved);

            const positioned = newOrder.map((s, idx) => ({
                ...s,
                position: idx + 1
            }));

            setSections(positioned);

            const payload = positioned.map(s => ({
                id: s.id,
                position: s.position
            }));

            try {
                const token = await getCsrfToken();
                await axios.put(
                    `${ADMIN_API_BASE}/course/${courseId}/section/reorder/`,
                    {sections: payload},
                    { headers: { 'csrf-token': token }, withCredentials: true }
                );
                showToast("ترتیب سرفصل‌ها ذخیره شد");
            } catch (err) {
                console.error("save reorder error", err);
                showToast("خطا در ذخیره ترتیب سرفصل‌ها", "error");
            }

            return;
        }

        // CONTENT REORDER – ONLY existing contents (with id)
        if (type.startsWith("CONTENT-")) {
            const sectionId = type.split("-")[1];
            const secIndex = sections.findIndex((s) => String(s.id) === String(sectionId));
            if (secIndex === -1) return;

            const sec = sections[secIndex];
            const allContents = Array.from(sec.contents || []);

            // only reorder among existing ones
            const existing = allContents.filter(c => c.id && !c._deleted);
            if (source.index >= existing.length || destination.index >= existing.length) return;

            const [moved] = existing.splice(source.index, 1);
            existing.splice(destination.index, 0, moved);

            // assign new levels
            const updatedExisting = existing.map((c, idx) => ({
                ...c,
                position: idx + 1
            }));

            // merge back into section (no tmp contents currently in list)
            const newSections = [...sections];
            newSections[secIndex] = {...sec, contents: updatedExisting};
            setSections(newSections);

            const reorderPayload = updatedExisting.map(c => ({
                id: c.id,
                position: c.position
            }));

            try {
                const token = await getCsrfToken();
                await axios.patch(
                    `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/reorder/`,
                    {contents: reorderPayload},
                    { headers: { 'csrf-token': token }, withCredentials: true }
                );
                showToast("ترتیب محتوا ذخیره شد");
            } catch (err) {
                console.error("Error saving content reorder:", err);
                showToast("خطا در ذخیره ترتیب محتوا", "error");
            }
        }
    };

    const toggleExpand = (sectionId) => {
        setExpanded(prev => ({...prev, [sectionId]: !prev[sectionId]}));
    };

    const onSaveFromEditModal = async (form) => {
        // check if section already exists
        const existingSection = sections.find(
            s => String(s.id) === String(form._originalId || form.id)
        );

        const isNew = !existingSection;

        const allContents = form.contents || [];

        const newContents = allContents.filter(
            c => c._isNew && !c._deleted
        );

        const deletedExistingIds = allContents
            .filter(c => !c._isNew && c._deleted)
            .map(c => c.id);

        const sectionPayload = {
            id: String(form.id || '').trim(),
            is_active: form.is_active,
            version: form.version
        };

        try {
            let section;

            if (isNew) {
                section = await handleCreateSection(sectionPayload);
            } else {
                section = await handleUpdateSection(
                    form._originalId || form.id,
                    sectionPayload
                );
            }

            const sid = section.id;

            if (newContents.length > 0) {
                await createContentsForSection(sid, newContents);
            }

            if (deletedExistingIds.length > 0) {
                await deleteContentsForSection(sid, deletedExistingIds);
            }

            await fetchSections();

            setOpenAddModal(false);
            setOpenEditModal(false);

            showToast('تغییرات ذخیره شد');
        } catch (err) {
            console.error(err);

            showToast(
                err.message || 'خطا در ذخیره‌سازی',
                'error'
            );
        }
    };

    const confirmDelete = async () => {
        if (!currentSection) return;
        await handleDeleteSection(currentSection.id);
    };

    const handleDeleteContent = async (sectionId, contentId) => {
        try {
            await axios.delete(
                `${ADMIN_API_BASE}/course/${courseId}/content/${contentId}`
            );

            setSections(prev =>
                prev.map(section =>
                    String(section.id) === String(sectionId)
                        ? {
                            ...section,
                            contents: section.contents.filter(
                                c => String(c.id) !== String(contentId)
                            )
                        }
                        : section
                )
            );

            showToast('محتوا حذف شد');
        } catch (err) {
            console.error(err);
            showToast('خطا در حذف محتوا', 'error');
        }
    };

    return (
        <div className="flex flex-1" {...rtlProps}>
            <div className="w-1/5">
                <AdminMenu path="./"/>
            </div>

            <div className="flex-grow w-4/5">
                <AdminHeader title={`سرفصل‌های دوره ${courseId}`}/>
                <div className="w-full px-10 my-8">
                    <div className="flex justify-between w-full items-center mb-6">
                        <div className="flex w-full gap-3">
                            <Link to={`/admin/course/${courseId}/section/add`} className="hidden">
                                hidden route
                            </Link>
                            <div className="flex w-full justify-between">
                                <Button
                                    onClick={() => setOpenAddModal(true)}
                                    className="bg-blue-600 text-white px-5 py-3 gap-2 rounded-md">
                                    <span>{TEXT.addSection}</span>
                                    <FontAwesomeIcon icon={faPlus} size={"lg"}/>
                                </Button>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center text-gray-600">در حال بارگذاری...</div>
                    ) : sections.length === 0 ? (
                        <div className="text-center text-gray-500 py-20">
                            <div className="text-6xl mb-4">📭</div>
                            <div className="text-lg mb-3">هیچ سرفصلی ثبت نشده است.</div>
                        </div>
                    ) : (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="all-sections" type="SECTION">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                                        {sections.map((s, idx) => (
                                            <SectionCard
                                                key={String(s.id)}
                                                s={s}
                                                index={idx}
                                                expanded={expanded}
                                                toggleExpand={toggleExpand}
                                                onEdit={openEdit}
                                                onDelete={openDelete}
                                                onDeleteContent={handleDeleteContent}
                                                courseId={courseId}
                                            />
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}

                    {toast && (
                        <Toast>
                            {toast.message}
                        </Toast>
                    )}

                    <Modal open={openAddModal} title="افزودن سرفصل جدید" onClose={() => setOpenAddModal(false)}>
                        <SectionEditForm
                            courseId={courseId}
                            initial={null}
                            onCancel={() => setOpenAddModal(false)}
                            onSave={onSaveFromEditModal}
                        />
                    </Modal>

                    <Modal open={openEditModal} title="ویرایش سرفصل" onClose={() => setOpenEditModal(false)}>
                        {currentSection && (
                            <SectionEditForm
                                courseId={courseId}
                                initial={currentSection}
                                onCancel={() => setOpenEditModal(false)}
                                onSave={onSaveFromEditModal}
                            />
                        )}
                    </Modal>

                    <Modal open={openDeleteModal} title="حذف سرفصل" onClose={() => setOpenDeleteModal(false)}>
                        <p>آیا از حذف این سرفصل مطمئن هستید؟ این کار تمام محتواهای داخل آن را حذف می‌کند.</p>
                        <div className="flex justify-end gap-2 mt-4">
                            <Button
                                onClick={() => setOpenDeleteModal(false)}
                                className="px-3 py-1 border rounded">
                                <span>انصراف</span>
                            </Button>
                            <Button
                                onClick={confirmDelete}
                                className="px-3 py-1 bg-red-600 text-white rounded">
                                <span>حذف</span>
                            </Button>
                        </div>
                    </Modal>
                </div>
            </div>
        </div>
    );
}
